import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WsContext = createContext(null);

export function WsProvider({ children }) {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const msgListeners = useRef(new Set());
  const wsRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
    } catch {
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    if (!user || !token) return;
    loadNotifications();
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/api/ws?token=${token}`);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (payload.event === "notification") {
          setNotifications((prev) => [payload.data, ...prev]);
          toast(payload.data.message, { description: "New notification" });
        } else if (payload.event === "message") {
          msgListeners.current.forEach((fn) => fn(payload.data));
        }
      } catch {
        // ignore malformed frames
      }
    };
    return () => {
      try { ws.close(); } catch { /* already closed */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const addMessageListener = useCallback((fn) => {
    msgListeners.current.add(fn);
    return () => msgListeners.current.delete(fn);
  }, []);

  const markRead = useCallback(async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    await api.post(`/notifications/${id}/read`);
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    await api.post("/notifications/read-all");
  }, []);

  const value = useMemo(() => ({
    notifications,
    unread: notifications.filter((n) => !n.read).length,
    addMessageListener, markRead, markAllRead, loadNotifications,
  }), [notifications, addMessageListener, markRead, markAllRead, loadNotifications]);

  return (
    <WsContext.Provider value={value}>
      {children}
    </WsContext.Provider>
  );
}

export const useWs = () => useContext(WsContext);
